import type { Theme, SxProps } from '@mui/material/styles';

import { useEffect } from 'react';
import { mergeClasses } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';

import { usePathname } from 'src/routes/hooks';

import { Logo as LogoBase } from 'src/components/logo';
import { Scrollbar as ScrollbarBase } from 'src/components/scrollbar';
import { NavSectionVertical as NavSectionVerticalBase } from 'src/components/nav-section';

import { layoutClasses } from '../core/classes';
import { NavUpgrade } from '../components/nav-upgrade';

// ----------------------------------------------------------------------

// Logo·Scrollbar·NavSectionVertical 은 아직 JS 라 props 가 전부 필수로 추론된다 — 실제 계약(전부 선택)으로 좁혀 쓴다 (로직 무변)
const Logo = LogoBase as unknown as React.ComponentType<{
  sx?: SxProps<Theme>;
  href?: string;
  isSingle?: boolean;
  disabled?: boolean;
  className?: string;
}>;

const Scrollbar = ScrollbarBase as unknown as React.ComponentType<{
  sx?: SxProps<Theme>;
  fillContent?: boolean;
  children?: React.ReactNode;
  className?: string;
}>;

const NavSectionVertical = NavSectionVerticalBase as unknown as React.ComponentType<
  {
    data?: unknown;
    sx?: SxProps<Theme>;
    checkPermissions?: (allowedRoles?: any) => boolean;
  } & Record<string, unknown>
>;

type ServiceNavMobileProps = {
  sx?: SxProps<Theme>;
  data?: unknown;
  open?: boolean;
  slots?: {
    topArea?: React.ReactNode;
    bottomArea?: React.ReactNode;
  };
  onClose: () => void;
  className?: string;
  checkPermissions?: (allowedRoles?: any) => boolean;
} & Record<string, unknown>;

export function NavMobile({
  sx,
  data,
  open,
  slots,
  onClose,
  className,
  checkPermissions,
  ...other
}: ServiceNavMobileProps) {
  const pathname = usePathname();

  useEffect(() => {
    if (open) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          className: mergeClasses([layoutClasses.nav.root, layoutClasses.nav.vertical, className]),
          sx: [
            {
              overflow: 'unset',
              bgcolor: 'var(--layout-nav-bg)',
              width: 'var(--layout-nav-mobile-width)',
              // ⚠️ 세로 flex 로 둔다 — 발치(`bottomArea`)가 `mt: 'auto'` 로 바닥에 붙으려면 필요하다
              display: 'flex',
              flexDirection: 'column',
            },
            ...(Array.isArray(sx) ? sx : [sx]),
          ],
        },
      }}
    >
      {slots?.topArea ?? (
        <Box sx={{ pl: 3.5, pt: 2.5, pb: 2 }}>
          <Logo />
        </Box>
      )}

      <Scrollbar fillContent>
        {/* 데스크톱 사이드바와 같은 규약 — 하위를 단 줄도 링크다(그쪽 주석) */}
        <NavSectionVertical
          data={data}
          checkPermissions={checkPermissions}
          enabledRootRedirect
          sx={{ px: 2, flex: '1 1 auto' }}
          {...other}
        />
      </Scrollbar>

      {/*
        ⚠️ **발치는 하나다** — 예전에는 흐르는 칸 안에도 카드를 그리고 밖에도 슬롯을 두어,
          슬롯을 넘기면 같은 카드가 두 번 섰다. 슬롯이 없을 때만 기본 카드를 세운다.
      */}
      {slots?.bottomArea ?? <NavUpgrade />}
    </Drawer>
  );
}
